import React from 'react';
import { ExternalLink, MapPin, ShieldCheck, Zap, Layers } from 'lucide-react';
import { useLanguage } from '../i18n/LanguageContext';
import { Section, Eyebrow, FadeIn, PageHero } from '../components/ui-bits';
import Breadcrumb from '../components/Breadcrumb';

const BodegajeIndustrial = () => {
  const { t, lang } = useLanguage();

  const features = [
    { icon: MapPin, title: t.v3Page.f1Title, body: t.v3Page.f1Body },
    { icon: ShieldCheck, title: t.v3Page.f2Title, body: t.v3Page.f2Body },
    { icon: Zap, title: t.v3Page.f3Title, body: t.v3Page.f3Body },
    { icon: Layers, title: t.v3Page.f4Title, body: t.v3Page.f4Body },
  ];

  return (
    <div data-testid="vertical-3-page">
      <Breadcrumb items={[
        { to: '/que-hacemos', label: lang === 'en' ? 'What We Do' : 'Qué Hacemos' },
        { to: null, label: t.verticals.v3.title },
      ]} />
      <PageHero eyebrow={t.v3Page.eyebrow} title={t.v3Page.title} intro={t.v3Page.intro} />

      <Section className="pb-12">
        <FadeIn>
          <div className="flex flex-wrap items-center gap-5">
            <span className="text-xs uppercase tracking-[0.2em] text-white/45">
              Operado por
            </span>
            <a
              href="https://www.absa-chile.cl"
              target="_blank"
              rel="noopener noreferrer"
              data-testid="chanarcillo-badge"
              className="inline-flex items-center gap-4 rounded-2xl border border-white/15 bg-white hover:bg-white/95 hover:border-amber-300/50 pl-3 pr-6 py-3 transition-colors"
            >
              <img
                src="https://customer-assets.emergentagent.com/job_bringing-light/artifacts/d8oqc226_Logo%20ABSA.png"
                alt="Centro Logístico y Productivo Chañarcillo – ABSA"
                className="h-14 w-auto object-contain"
                loading="lazy"
              />
            </a>
          </div>
        </FadeIn>
      </Section>

      <Section className="pb-24" dataTestId="v3-image-block">
        <FadeIn>
          <div className="relative rounded-3xl overflow-hidden border border-white/10 aspect-[16/9]">
            <img
              src="https://customer-assets.emergentagent.com/job_bringing-light/artifacts/zn6jpw9c_ABSA.png"
              alt="Vista aérea del Centro Logístico y Productivo Chañarcillo"
              className="absolute inset-0 w-full h-full object-cover"
            />
            <div className="absolute inset-0 bg-gradient-to-t from-[#070d18] via-[#070d18]/20 to-transparent" />
            <div className="absolute bottom-8 left-8 right-8">
              <div className="text-xs uppercase tracking-[0.18em] text-amber-300/85">Centro Chañarcillo · ABSA Chile</div>
            </div>
          </div>
        </FadeIn>
      </Section>

      <Section className="pb-24" dataTestId="v3-features">
        <FadeIn>
          <Eyebrow>{t.v3Page.features}</Eyebrow>
        </FadeIn>
        <div className="mt-12 grid grid-cols-1 md:grid-cols-2 gap-5">
          {features.map((f, i) => {
            const Icon = f.icon;
            return (
              <FadeIn key={i} delay={i * 0.07}>
                <div className="rounded-2xl border border-white/10 bg-white/[0.025] p-8 h-full card-hover" data-testid={`v3-feature-${i + 1}`}>
                  <div className="w-11 h-11 rounded-xl bg-amber-300/10 border border-amber-300/20 flex items-center justify-center text-amber-300">
                    <Icon className="w-5 h-5" />
                  </div>
                  <h4 className="mt-6 font-display font-bold text-xl text-white tracking-tight">{f.title}</h4>
                  <p className="mt-3 font-body text-white/55 leading-relaxed">{f.body}</p>
                </div>
              </FadeIn>
            );
          })}
        </div>
      </Section>

      <Section className="pb-32">
        <FadeIn>
          <a
            href="https://www.absa-chile.cl"
            target="_blank"
            rel="noopener noreferrer"
            className="inline-flex items-center gap-2 px-7 h-12 rounded-full bg-amber-300 text-[#0a1322] font-semibold text-sm hover:bg-amber-200 transition-colors"
            data-testid="v3-absa-link"
          >
            absa-chile.cl <ExternalLink className="w-4 h-4" />
          </a>
        </FadeIn>
      </Section>
    </div>
  );
};

export default BodegajeIndustrial;
